import React from 'react';
import clsx from 'clsx';

import Collapser from '../Collapser';
import { twMerge } from '@/utilities';

export type CardProps = {
  title?: React.ReactNode;
  titleClassName?: string;
  headerClassName?: string;

  headerRight?: React.ReactNode;
  footer?: React.ReactNode;
  footerClassName?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  className?: string;
  children?: React.ReactNode;
  /** max height for the scrollable content area (CSS units or number -> px) */
  maxContentHeight?: string | number;
  contentClassName?: string;
};

const Card: React.FC<CardProps> = ({
  title,
  titleClassName = 'text-lg font-semibold text-text',

  headerRight,
  headerClassName,

  footer,
  footerClassName,

  collapsible = false,
  defaultCollapsed = false,
  className = '',
  children,
  maxContentHeight = '',
  contentClassName = '',
}) => {
  const contentStyle: React.CSSProperties = {};
  if (typeof maxContentHeight === 'number') {
    contentStyle.maxHeight = `${maxContentHeight}px`;
  } else if (maxContentHeight) {
    contentStyle.maxHeight = maxContentHeight;
  }

  const header = (
    <div
      className={clsx('flex items-center justify-between w-full px-md py-md')}
    >
      <div className={clsx('', titleClassName)}>{title}</div>
      <div className="flex items-center ml-sm">{headerRight}</div>
    </div>
  );

  const content = (
    <div
      style={contentStyle}
      className={clsx(contentClassName, `p-md overflow-y-auto`)}
    >
      {children}
    </div>
  );

  return (
    <div
      className={clsx(
        className,
        `bg-content rounded-lg border border-border shadow-sm`
      )}
    >
      {collapsible ? (
        <Collapser
          header={header}
          headerClassName="w-full"
          topClassName={headerClassName}
          defaultCollapsed={defaultCollapsed}
        >
          {content}
        </Collapser>
      ) : (
        <>
          <div className="flex items-center justify-between px-md py-md border-b border-border">
            {header}
          </div>
          {content}
        </>
      )}

      {footer && (
        <div
          className={twMerge(
            'px-md py-md border-t border-border overflow-hidden rounded-b-lg',
            footerClassName
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export default React.memo(Card) as typeof Card;
